<script lang="ts">
  import { onMount } from "svelte"
  import { slide } from "svelte/transition"
  import { xhr, toast, formatDate } from "$app/utils"
  import Button from "$app/components/ButtonSpinner.svelte"
  import EditGoalStatus from "$app/Pages/Plans/Goals/EditGoalStatus.svelte"
  import {
    FileText,
    GitBranch,
    AlertTriangle,
    ChevronRight
  } from "lucide-svelte"
  import { appStore } from "$app/store.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import Tiptap from "$app/components/Tiptap.svelte"
  import { onlyTextButtons } from "$app/components/Tiptap/buttons"

  interface Props {
    goal: GoalType
    loadParent?: boolean
    parentGoals?: GoalType[]
    parentGoal?: GoalType
    oncreate?: (g: GoalType) => void
  }

  let {
    goal,
    loadParent = false,
    parentGoals = $bindable([]),
    parentGoal,
    oncreate
  }: Props = $props()

  const departments = appStore.departments
  const assignees = departments.map((d) => {
    return { id: d.id, value: d.id, label: d.name, name: d.name }
  })
  const assignee = assignees.find((a) => a.id === goal.assignee_id) || null

  const plan: PlanType = appStore.plan
  const current_user: UserType = appStore.current_user
  const dates = appStore.dates
  const year = dates.next_date_end.split("-")[0]
  const channel_id: string = appStore.channel_id

  let show_description = $state(false)
  let description = $state("")
  let show_leadership_notes = $state(false)
  let leadership_notes = $state("")
  let show_obstacles = $state(false)
  let obstacles_text = $state("")

  function toggleDescription() {
    if (show_description) {
      description = form.description
    }
    show_description = !show_description
  }

  function toggleLeadershipNotes() {
    if (show_leadership_notes) {
      leadership_notes = form.leadership_notes
    }
    show_leadership_notes = !show_leadership_notes
  }

  function toggleObstacles() {
    if (show_obstacles) {
      obstacles_text = form.obstacles_text
    }
    show_obstacles = !show_obstacles
  }

  let errors: any = $state({})

  const initialForm = {
    name: goal.name || "",
    type: goal.type || "",
    expected_at: goal.expected_at || null,
    description: goal.description || "",
    obstacles_text: goal.obstacles_text || "",
    leadership_notes: goal.leadership_notes || "",
    assignee_type: goal.assignee_type || "Department",
    assignee_id: goal.assignee_id,
    status: goal.status || "on_track",
    visibility: goal.visibility || "plan",
    parent_goal_id: goal.parent_goal_id,
    parent_goal_type: goal.parent_goal_type
  }

  let form = $state({ ...initialForm } as GoalType)
  let loading = $state(false)

  async function loadParentGoals(): Promise<void> {
    loading = true
    const filterGoalType = {
      AnnualGoal: "LongTermGoal",
      QuarterlyGoal: "AnnualGoal"
    }[goal.type]

    try {
      const resp = await xhr.get<{ goals: GoalType[] }>(
        `/plans/${plan.id}/goals/new.json?expected_at=${dates.next_year_end}&type=${goal.type}`
      )

      parentGoals = resp.data.goals
        .filter((g) => g.type === filterGoalType)
        .sort((a, b) => a.assignee_position - b.assignee_position)
    } catch (e: any) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading the goal form", "error")
    } finally {
      loading = false
    }
  }

  // Save New Goal
  let saving = $state(false)
  async function createGoal(): Promise<void> {
    saving = true

    errors = {}

    try {
      const parentGoal = parentGoals.find((g) => g.id === form.parent_goal_id)
      if (!parentGoal) {
        errors = { parent_goal_id: "Select the parent goal" }
        return
      }

      const resp = await xhr.post<GoalType>(`/plans/${plan.id}/goals.json`, {
        goal: { ...form, parent_goal_type: parentGoal.type },
        channel: channel_id
      })
      oncreate(resp.data)
    } catch (e: any) {
      toast("There was an error creating the new goal", "error")
      if (e.response && e.response.data) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        if (typeof Honeybadger.notify !== "undefined") {
          Honeybadger.notify(e)
        }
      }
    } finally {
      saving = false
    }
  }

  export function changesCount(): number {
    let count = 0
    Object.keys(initialForm).forEach((key) => {
      if (initialForm[key] !== form[key]) {
        count++
      }
    })

    return count
  }

  const goalD = {
    AnnualGoal: { label: "Annual Goal", css: "annual-goal" },
    QuarterlyGoal: { label: "Quarterly Goal", css: "quarterly-goal" }
  }[goal.type]

  onMount(async () => {
    if (loadParent) {
      loadParentGoals()
    }
  })
</script>

<div class="mt-3 sm:mt-5 overflow-visible">
  <div class="flex items-center">
    <h3 class="text-2xl leading-6 font-medium text-gray-500">
      Create New
      <span class="font-semibold text-{goalD.css}">
        {goalD.label}
      </span>
    </h3>
  </div>

  <div class="mt-6">
    <div class="grid grid-cols-6 gap-y-3 gap-x-4">
      <div class="col-span-6">
        <p class="mb-1">Name</p>
        <div class="input-cont" class:error={errors.name}>
          <input
            id="goal_name"
            type="text"
            bind:value={form.name}
            class="input"
          />
          <div class="msg">{errors.name}</div>
        </div>
      </div>

      <div class="col-span-6 sm:col-span-3">
        <label for="parent_goal_id" class="label">Parent Goal </label>
        <div class="input-cont" class:error={errors.parent_goal_id}>
          {#if loading}
            Loading parent goals...
          {:else if parentGoal}
            {parentGoal.name}
          {:else}
            <select id="parent_goal_id" bind:value={form.parent_goal_id}>
              {#each parentGoals as pg (pg.id)}
                <option value={pg.id}>{pg.name}</option>
              {:else}
                <option value="">No Parent Goals for current year</option>
              {/each}
            </select>
          {/if}
          <div class="msg">{errors.parent_goal_id}</div>
        </div>
      </div>

      {#if current_user.role === "leader" || current_user.role === "admin"}
        <div class="col-span-6 sm:col-span-3" class:error={errors.type}>
          <label for="visibility" class="label">Goal Visibility</label>
          <div class="input-cont" class:error={errors.visibility}>
            <select id="goal_visibility" bind:value={form.visibility}>
              <option value="plan">All Members</option>
              <option value="leadership">Only Leadership Team</option>
            </select>
            <div class="msg">{errors.visibility}</div>
          </div>
        </div>
      {/if}

      <div class="col-span-6 sm:col-span-3" style="z-index: 210;">
        <label for="status" class="label">Status</label>
        <div class="w-full">
          <EditGoalStatus
            id="goal_status"
            type="Goal"
            goalOrTask={form}
            update={false}
            onupdate={(t) => (form.status = t.status)}
          />
        </div>
      </div>

      <div class="col-span-6 sm:col-span-3 relative" style="z-index: 200;">
        <label for="expected_at" class="label">Expected By</label>
        <div class="input-cont" class:error={errors.expected_at}>
          {#if goal.type === "QuarterlyGoal"}
            <input type="date" class="input" bind:value={form.expected_at} />
          {:else}
            {formatDate(goal.expected_at)}
          {/if}
          <div class="msg">{errors.expected_at}</div>
        </div>
      </div>

      <div class="col-span-6 sm:col-span-3" class:error={errors.assignee_id}>
        <label for="assignee_id" class="label">Assignee</label>
        <div
          class="mt-1 input-cont assignee-cont"
          class:error={errors.assignee}
        >
          <select id="goal_assignee_id" bind:value={form.assignee_id}>
            {#if departments.length}
              {#each departments as dept (dept.id)}
                <option value={dept.id}>{dept.name}</option>
              {/each}
            {:else}
              <option value="">No Departments</option>
            {/if}
          </select>
          <div class="msg">{errors.assignee}</div>
        </div>
      </div>

      <div class="col-span-6">
        <div class="flex items-center mb-1 mt-2">
          <button
            id="toggle-description"
            class="flex items-center rotate-icon"
            onclick={toggleDescription}
            class:open={show_description}
          >
            <ChevronRight class="text-gray-500 mr-2 size-5 rotate-icon-90" />

            <FileText class="text-gray-400 mr-2 h-4 w-4" />
            <div class="text-sm text-gray-700">Description / Action Plan</div>
          </button>
        </div>
        {#if show_description}
          <div transition:slide class="pl-5 pt-1">
            <Tiptap
              onupdate={({ html }) => (form.description = html)}
              placeholder="Enter a detailed description for this goal"
              class="h-20"
              content={description}
              id="goal_description"
              buttons={onlyTextButtons}
            />
          </div>
        {/if}
      </div>

      {#if current_user.role === "leader" || current_user.role === "admin"}
        <div class="col-span-6">
          <div class="flex items-center mb-1">
            <button
              id="toggle-leadership-notes"
              class="flex items-center rotate-icon"
              onclick={toggleLeadershipNotes}
              class:open={show_leadership_notes}
            >
              <ChevronRight class="text-gray-500 mr-2 size-5 rotate-icon-90" />

              <GitBranch class="text-gray-400 mr-2 h-4 w-4" />
              <div class="text-sm text-gray-700">Leadership Team Notes</div>
            </button>
          </div>
          {#if show_leadership_notes}
            <div transition:slide class="pl-5 pt-1">
              <Tiptap
                id="goal_leadership_notes"
                buttons={onlyTextButtons}
                content={leadership_notes}
                placeholder="Enter any private notes here which are visible only for Leadership Team members."
                onupdate={({ html }) => (form.leadership_notes = html)}
              />
            </div>
          {/if}
        </div>
      {/if}

      <div class="col-span-6">
        <div class="flex items-center mb-1">
          <button
            id="toggle-obstacles"
            class="flex items-center rotate-icon"
            onclick={toggleObstacles}
            class:open={show_obstacles}
          >
            <ChevronRight class="text-gray-500 mr-2 size-5 rotate-icon-90" />
            <AlertTriangle class="text-gray-400 mr-2 h-4 w-4" />
            <div class="text-sm text-gray-700">Obstacles</div>
          </button>
        </div>

        {#if show_obstacles}
          <div transition:slide class="pl-5 pt-1">
            <Tiptap
              onupdate={({ html }) => (form.obstacles_text = html)}
              placeholder="Enter any obstacles to accomplishing this goal"
              class="h-20"
              content={obstacles_text}
              id="goal_description"
              buttons={onlyTextButtons}
            />
          </div>
        {/if}
      </div>
      <div class="col-span-6 py-3 text-right mt-2">
        <Button
          class="btn btn-rounded success btn-big"
          processing={saving}
          onclick={() => {
            createGoal()
          }}
        >
          Create Goal
        </Button>
      </div>
    </div>
  </div>
</div>

<style>
  .currency {
    border-top-right-radius: 0.375rem;
    border-bottom-right-radius: 0.375rem;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
  .percent {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-top-left-radius: 0.375rem;
    border-bottom-left-radius: 0.375rem;
  }
</style>
